import React, { useEffect, useMemo, useState } from 'react';

import type { TranscriptionSegment } from '@/services/types/transcript-types';

import { TranscriptSection } from './TranscriptSection';

type ConversationLike = {
  id: string;
  name?: string;
  start_timestamp?: string;
  created_at?: string;
  source?: string;
  recording_url?: string | null;
};

interface StackedTranscriptPaneProps {
  matterId: string;
  conversations: ConversationLike[];
  /** Hover key from left pane; when set, snap to first match across the stack */
  activeHoverKey: string | null;
  /** Provide search terms to look for, given a hover key */
  getSearchTerms: (key: string) => string[];
  /** Report whether each hover key has any evidence across the stack */
  onEvidenceComputed?: (map: Record<string, boolean>) => void;
  /** Optional: override the list of keys to compute evidence for */
  evidenceKeys?: readonly string[];
}

const EVIDENCE_KEYS = [
  'final:policy',
  'final:claim',
  'final:adjName',
  'final:adjEmail',
  'final:adjPhone',
  'final:adjFax',
] as const;

// type EvidenceKey = (typeof EVIDENCE_KEYS)[number];

export const StackedTranscriptPane: React.FC<StackedTranscriptPaneProps> = ({
  matterId,
  conversations,
  activeHoverKey,
  getSearchTerms,
  onEvidenceComputed,
  evidenceKeys,
}) => {
  // Filter to supported conversation sources (Bland + Dialpad), most recent first
  const blandConversations = useMemo(() => {
    const list = (conversations || []).filter((c) => {
      const src = String(c.source || '').toLowerCase();
      return src.includes('bland') || src.includes('dialpad');
    });
    return [...list].sort((a, b) => {
      const at = new Date(a.start_timestamp || a.created_at || 0).getTime();
      const bt = new Date(b.start_timestamp || b.created_at || 0).getTime();
      return bt - at;
    });
  }, [conversations]);

  // Per-conversation segments cache from child sections
  const [segmentsByConversation, setSegmentsByConversation] = useState<
    Record<string, TranscriptionSegment[]>
  >({});

  // Per-conversation focused segment to highlight
  const [focusByConversation, setFocusByConversation] = useState<
    Record<string, string | null>
  >({});

  // Global matches across the stack for current hover key
  const [globalMatches, setGlobalMatches] = useState<
    Array<{ conversationId: string; segmentId: string }>
  >([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState<number>(0);

  // Helper: find first segment id in a transcript containing any term
  const findMatchInSegments = (
    segs: TranscriptionSegment[],
    terms: string[],
  ): string | null => {
    if (!segs.length || !terms.length) return null;
    const lowerSegs = segs.map((s) => ({
      id: s.id,
      text: s.text.toLowerCase(),
    }));
    const lowerTerms = terms
      .map((t) => t.toLowerCase())
      .filter((t) => t.length >= 3);
    for (let i = 0; i < lowerTerms.length; i += 1) {
      const q = lowerTerms[i];
      const hit = lowerSegs.find((s) => s.text.includes(q));
      if (hit) return hit.id;
    }
    return null;
  };

  // Recompute evidence map and global matches whenever inputs change
  useEffect(() => {
    const evidence: Record<string, boolean> = {};
    const keys =
      evidenceKeys && evidenceKeys.length > 0
        ? evidenceKeys
        : (EVIDENCE_KEYS as readonly string[]);
    (keys as readonly string[]).forEach((k) => {
      evidence[k] = false;
    });

    const matches: Array<{ conversationId: string; segmentId: string }> = [];
    const termsForHover = activeHoverKey ? getSearchTerms(activeHoverKey) : [];

    blandConversations.forEach((c) => {
      const segs = segmentsByConversation[c.id] || [];
      // Evidence computation across all keys
      (keys as readonly string[]).forEach((k) => {
        if (evidence[k]) return; // already true
        const terms = getSearchTerms(k);
        if (!terms.length) return;
        const hit = findMatchInSegments(segs, terms);
        if (hit) evidence[k] = true;
      });

      // Global matches for the current hover key
      if (activeHoverKey && termsForHover.length) {
        const hit = findMatchInSegments(segs, termsForHover);
        if (hit) matches.push({ conversationId: c.id, segmentId: hit });
      }
    });

    setGlobalMatches(matches);
    setActiveMatchIndex(0);
    onEvidenceComputed?.(evidence);

    // Snap to first match when available
    if (matches.length > 0) {
      const first = matches[0];
      setFocusByConversation((prev) => ({
        ...prev,
        [first.conversationId]: first.segmentId,
      }));
    } else if (activeHoverKey) {
      // clear any focus when hover changes and no matches
      setFocusByConversation({});
    }
  }, [
    blandConversations,
    segmentsByConversation,
    activeHoverKey,
    getSearchTerms,
    onEvidenceComputed,
    evidenceKeys,
  ]);

  const gotoPrevMatch = () => {
    if (globalMatches.length <= 1) return;
    setActiveMatchIndex((cur) => {
      const next = cur <= 0 ? globalMatches.length - 1 : cur - 1;
      const m = globalMatches[next];
      if (m) setFocusByConversation({ [m.conversationId]: m.segmentId });
      return next;
    });
  };

  const gotoNextMatch = () => {
    if (globalMatches.length <= 1) return;
    setActiveMatchIndex((cur) => {
      const next = cur >= globalMatches.length - 1 ? 0 : cur + 1;
      const m = globalMatches[next];
      if (m) setFocusByConversation({ [m.conversationId]: m.segmentId });
      return next;
    });
  };

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
      <div className="p-3 border-b">
        <div className="text-sm font-semibold">Bland transcripts</div>
        <div className="text-xs text-gray-500">
          {blandConversations.length} transcript
          {blandConversations.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 p-3">
        {blandConversations.map((c) => (
          <TranscriptSection
            key={c.id}
            matterId={matterId}
            conversation={c}
            focusSegmentId={focusByConversation[c.id] || null}
            onSegmentsSet={(segs) =>
              setSegmentsByConversation((prev) => ({ ...prev, [c.id]: segs }))
            }
          />
        ))}
      </div>
      {globalMatches.length > 1 && (
        <div className="px-3 py-2 border-t bg-white flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {activeMatchIndex + 1} of {globalMatches.length} matches
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors"
              onClick={gotoPrevMatch}
            >
              Previous
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors"
              onClick={gotoNextMatch}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
